// success-criteria-drawer — the assignment "Success criteria" self-tick drawer
// (assignment-success-criteria [4.3]). A re-implementation of the challenge
// "What to add" drawer (challenge-chrome.js `wireRequirementsDrawer`) for the
// assignment surface: a left-edge overlay on the code pane with a peek tab that
// slides a panel of the teacher-authored success criteria over the editor. Each
// row is self-tickable with an optional collapsible detail. Ticks are the
// student's OWN progress — they persist on the attempt (checklistTicks, debounced
// POST) and NEVER gate grading or submission.
//
// Shared by BOTH the student assignment surface (assignment-chrome.js) and the
// read-only teacher review surface (review-chrome.js) via the `readOnly` flag:
// read-only renders the rows + tick state, disables clicking, and hides the save
// note. The shipped challenge drawer is left untouched (this is a sibling, not a
// refactor — decisions.md "keep the challenge path byte-identical").
//
//   wireSuccessCriteriaDrawer(opts)
//     opts.doc        — the document (ctx.doc)
//     opts.criteria   — [{ text, detail }] teacher-authored criteria
//     opts.ticks      — [Number] saved ticked criterion indices
//     opts.readOnly   — true on the review surface (no clicking, no save)
//     opts.saveTicks  — fn(indices:[Number]) -> Promise, called debounced on tick
//                       (omitted / ignored when readOnly)
//     opts.idPrefix   — element id prefix (default 'assignment-drawer')

var DW_CHECK_SVG = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5"/></svg>';
var DW_CHEV_SVG = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m9 18 6-6-6-6"/></svg>';

// Defensive tick-indexing (the [4.2] rule): `savedTicks` are criterion INDICES, so
// build the ticked flags by asking, for each criterion that EXISTS, whether its index
// is in the saved list. This renders exactly `criteria.length` rows and silently
// ignores any out-of-range saved index (e.g. after the teacher shortened the list
// post-submission) — never a crash, never a phantom row. Pure/exported for testing.
export function computeTicked(criteria, savedTicks) {
  var ticks = Array.isArray(savedTicks) ? savedTicks : [];
  return (Array.isArray(criteria) ? criteria : []).map(function (_c, i) {
    return ticks.indexOf(i) !== -1;
  });
}

export function wireSuccessCriteriaDrawer(opts) {
  opts = opts || {};
  var doc = opts.doc || document;
  var criteria = Array.isArray(opts.criteria) ? opts.criteria : [];
  // Reveal rule: no criteria => plain assignment, drawer stays hidden.
  if (!criteria.length) { return; }

  var readOnly = !!opts.readOnly;
  var prefix = opts.idPrefix || 'assignment-drawer';
  var savedTicks = Array.isArray(opts.ticks) ? opts.ticks : [];

  var drawer = doc.getElementById(prefix);
  var tab = doc.getElementById(prefix + '-tab');
  var rowsEl = doc.getElementById(prefix + '-rows');
  var fillEl = doc.getElementById(prefix + '-fill');
  var countEl = doc.getElementById(prefix + '-count');
  var tabCountEl = doc.getElementById(prefix + '-tabcount');
  var saveNote = doc.getElementById(prefix + '-save');
  var saveText = doc.getElementById(prefix + '-save-text');
  if (!drawer || !tab || !rowsEl) { return; }

  var total = criteria.length;
  // Defensive tick-indexing (see computeTicked): only existing criteria get a row; a
  // stale saved index out of range is ignored.
  var ticked = computeTicked(criteria, savedTicks);

  function doneCount() {
    return ticked.reduce(function (n, t) { return n + (t ? 1 : 0); }, 0);
  }
  function refreshProgress() {
    var done = doneCount();
    if (countEl) { countEl.textContent = done + ' / ' + total; }
    if (tabCountEl) { tabCountEl.textContent = done + '/' + total; }
    if (fillEl) { fillEl.style.transform = 'scaleX(' + (total ? done / total : 0) + ')'; }
  }

  // Debounced persistence of the current ticks to the student's OWN attempt.
  // Optimistic: the UI updates immediately; a failed save keeps the local state and
  // surfaces a quiet retry note (the next tick re-attempts the save). Never runs
  // on the read-only review surface.
  var saveTimer = null;
  function doSave() {
    if (readOnly || typeof opts.saveTicks !== 'function') { return Promise.resolve(); }
    var indices = [];
    ticked.forEach(function (t, i) { if (t) { indices.push(i); } });
    return opts.saveTicks(indices)
      .then(function () {
        if (saveText) { saveText.textContent = 'Progress saved to your account'; }
      })
      .catch(function (err) {
        if (saveText) { saveText.textContent = 'Couldn’t save — we’ll retry'; }
        if (saveNote) { saveNote.classList.add('is-error'); }
        console.error(err);
      });
  }
  function persistTicks() {
    if (readOnly) { return; }
    if (saveTimer) { clearTimeout(saveTimer); }
    if (saveText) { saveText.textContent = 'Saving your progress…'; }
    if (saveNote) { saveNote.classList.remove('is-error'); }
    saveTimer = setTimeout(function () { saveTimer = null; doSave(); }, 600);
  }
  // If the student switches away or closes the tab within the debounce window, flush
  // the pending save immediately (keepalive lets it finish during unload).
  function flushTicks() {
    if (saveTimer) { clearTimeout(saveTimer); saveTimer = null; doSave(); }
  }
  if (!readOnly) {
    doc.addEventListener('visibilitychange', function () {
      if (doc.visibilityState === 'hidden') { flushTicks(); }
    });
    (doc.defaultView || window).addEventListener('pagehide', flushTicks);
  }

  criteria.forEach(function (crit, i) {
    var li = doc.createElement('li');
    li.className = 'dw-check' + (ticked[i] ? ' is-done' : '') + (readOnly ? ' is-readonly' : '');

    // The tick target — a real button (box + text) so it is keyboard-operable. On the
    // read-only review surface it renders disabled (state only, no interaction).
    var main = doc.createElement('button');
    main.type = 'button';
    main.className = 'dw-check-main';
    main.setAttribute('aria-pressed', ticked[i] ? 'true' : 'false');
    if (readOnly) { main.disabled = true; }

    var box = doc.createElement('span');
    box.className = 'dw-box';
    box.setAttribute('aria-hidden', 'true');
    box.innerHTML = DW_CHECK_SVG;
    main.appendChild(box);

    var txt = doc.createElement('span');
    txt.className = 'dw-txt';
    var name = doc.createElement('span');
    name.className = 'dw-name';
    name.textContent = (crit && crit.text && String(crit.text).trim())
      ? String(crit.text).trim() : ('Criterion ' + (i + 1));
    txt.appendChild(name);
    main.appendChild(txt);
    li.appendChild(main);

    if (!readOnly) {
      main.addEventListener('click', function () {
        ticked[i] = !ticked[i];
        li.classList.toggle('is-done', ticked[i]);
        main.setAttribute('aria-pressed', ticked[i] ? 'true' : 'false');
        refreshProgress();
        persistTicks();
      });
    }

    // Optional collapsible detail — its own row BELOW the tick target (a sibling, not
    // nested inside the button), collapsed by default; no control when the teacher
    // left the detail blank.
    if (crit && crit.detail && String(crit.detail).trim()) {
      var detailRow = doc.createElement('div');
      detailRow.className = 'dw-hint-row';
      var toggle = doc.createElement('button');
      toggle.type = 'button';
      toggle.className = 'dw-hint-toggle';
      toggle.setAttribute('aria-expanded', 'false');
      toggle.innerHTML = DW_CHEV_SVG + '<span>Detail</span>';
      var detailBody = doc.createElement('div');
      detailBody.className = 'dw-hint-body';
      detailBody.hidden = true;
      detailBody.textContent = String(crit.detail).trim();
      toggle.addEventListener('click', function () {
        var willOpen = detailBody.hidden;
        detailBody.hidden = !willOpen;
        toggle.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
      });
      detailRow.appendChild(toggle);
      detailRow.appendChild(detailBody);
      li.appendChild(detailRow);
    }

    rowsEl.appendChild(li);
  });

  // The save note is meaningless read-only — hide it.
  if (readOnly && saveNote) { saveNote.hidden = true; }

  function setOpen(open) {
    drawer.classList.toggle('is-open', open);
    tab.setAttribute('aria-expanded', open ? 'true' : 'false');
  }
  tab.addEventListener('click', function () {
    setOpen(!drawer.classList.contains('is-open'));
  });

  // what-to-add-drawer-arrow-and-dismiss (Trello cqn6yxJR): a press inside the code
  // editor closes an already-open drawer. Applies in both readOnly and editable mode.
  // Document-level CAPTURE phase so Ace's own mouse handlers cannot swallow it first;
  // never prevented/stopped, so the press still places the caret. Close-only — no toggle.
  doc.addEventListener('pointerdown', function (e) {
    var t = e && e.target;
    if (!(t && typeof t.closest === 'function' && t.closest('.editor-host'))) { return; }
    if (drawer.classList.contains('is-open')) { setOpen(false); }
  }, true);

  refreshProgress();
  drawer.hidden = false;
  // Open on first paint so the student sees the criteria; the tab tucks it away.
  setOpen(true);
}
